import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Star, ShieldCheck, Radio, Bike, IdCard, FileText, Wrench,
  MapPin, PhoneCall, User, Wallet, BarChart3, Settings, Trophy, AlertTriangle
} from "lucide-react";

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`; }

function StatusPill({ status }) {
  const map = {
    valid:   { label: "Valide",       cls: "bg-emerald-50 text-emerald-700" },
    warning: { label: "Bientôt",      cls: "bg-amber-50 text-amber-700" },
    expired: { label: "À renouveler", cls: "bg-red-50 text-red-700" },
    missing: { label: "Manquant",     cls: "bg-slate-100 text-slate-500" },
    unknown: { label: "?",            cls: "bg-slate-100 text-slate-500" },
  };
  const v = map[status] || map.unknown;
  return <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-lg ${v.cls}`}>{v.label}</span>;
}

function DocRow({ icon: Icon, title, doc, testId }) {
  const dateLabel = doc?.expires_at
    ? new Date(doc.expires_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })
    : "Aucune date renseignée";
  const prefix = doc?.status === "expired" ? "Expiré le" : "Expire le";
  return (
    <div className="flex items-center gap-3 py-2.5 border-t border-dashed border-[#E4E0D8] first:border-t-0 first:pt-0" data-testid={testId}>
      <div className="w-9 h-9 rounded-xl bg-[#F7F5F1] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#0F2B46]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#142433]">{title}</div>
        <div className="text-[11.5px] text-[#5B6B7A]">{doc?.expires_at ? `${prefix} ${dateLabel}` : dateLabel}</div>
      </div>
      <StatusPill status={doc?.status} />
    </div>
  );
}

export default function DriverExpressDashboard() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = () => {
    api.get("/driver/express/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Impossible de charger le dashboard"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleOnline = async () => {
    if (!data?.express_enabled) {
      toast.error("Trigo Express non activé pour votre compte. Contactez l'administrateur.");
      return;
    }
    setToggling(true);
    try {
      const next = !data.is_online;
      await api.post("/driver/express/toggle-online", { online: next });
      // Persist the driver's intent across sessions. When set, the app will
      // auto-reactivate the Express connection at next boot even if the
      // browser was fully closed or the device slept.
      try {
        if (next) localStorage.setItem("trigo:express-persistent", "1");
        else localStorage.removeItem("trigo:express-persistent");
      } catch { /* private mode */ }
      // Wake Lock: keep the screen alive while online (Chrome/Safari 16+).
      try {
        if (next && "wakeLock" in navigator) {
          const wl = await navigator.wakeLock.request("screen");
          window.__trigoExpressWakeLock = wl;
        } else if (!next && window.__trigoExpressWakeLock) {
          await window.__trigoExpressWakeLock.release();
          window.__trigoExpressWakeLock = null;
        }
      } catch { /* not supported / battery saver */ }
      if (next && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => api.post("/location/update", { lat: pos.coords.latitude, lng: pos.coords.longitude, booking_id: "" }).catch(() => {}),
          () => {},
          { enableHighAccuracy: true, timeout: 6000 }
        );
      }
      setData((prev) => ({ ...prev, is_online: next }));
      checkAuth?.();
      toast.success(next ? "Trigo Express activé (persiste au redémarrage)" : "Vous êtes hors ligne");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setToggling(false); }
  };

  // Auto-reactivate Express connection on mount if the driver had it ON before.
  useEffect(() => {
    if (!data) return;
    if (!data.express_enabled) return;
    if (data.is_online) return; // already online, nothing to do
    let flag = "0";
    try { flag = localStorage.getItem("trigo:express-persistent") || "0"; } catch { /* noop */ }
    if (flag !== "1") return;
    // The driver had Express ON before. Reactivate silently.
    api.post("/driver/express/toggle-online", { online: true })
      .then(() => setData((prev) => ({ ...prev, is_online: true })))
      .catch(() => {});
    // Also push one fresh location so the admin map + matcher immediately re-includes them
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => api.post("/location/update", { lat: pos.coords.latitude, lng: pos.coords.longitude, booking_id: "" }).catch(() => {}),
        () => {},
        { enableHighAccuracy: true, timeout: 6000 }
      );
    }
  }, [data?.express_enabled, data?.is_online]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(180deg,#0B1F33,#0F2B46 40%,#0B1F33)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-white/70" />
      </div>
    );
  }
  if (!data) return null;

  const { profile, stats, level, earnings, vehicle, documents, safety, express_enabled, is_online } = data;
  const initials = (profile.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen py-6 px-4" style={{ background: "linear-gradient(180deg,#0B1F33,#0F2B46 40%,#0B1F33)" }}>
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate("/driver")} className="flex items-center gap-1 text-white/80 text-sm mb-3 hover:text-white" data-testid="driver-express-back-btn">
          <ArrowLeft className="w-4 h-4" /> Retour au dashboard
        </button>

        <div className="rounded-[36px] overflow-hidden shadow-2xl border-2 border-[#0B1F33] bg-[#F7F5F1] flex flex-col" data-testid="driver-express-dashboard">
          {/* Header navy */}
          <div className="relative px-5 pt-4 pb-12" style={{ background: "linear-gradient(160deg,#0F2B46,#173A5E)" }}>
            {/* Online toggle */}
            <button
              onClick={toggleOnline}
              disabled={!express_enabled || toggling}
              className={`absolute right-4 top-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border ${
                is_online
                  ? "bg-emerald-500/15 border-emerald-400 text-emerald-300"
                  : express_enabled
                    ? "bg-white/10 border-white/40 text-white/85 hover:bg-white/20"
                    : "bg-white/5 border-white/20 text-white/50"
              }`}
              style={{ fontFamily: "Sora" }}
              data-testid="driver-express-online-toggle"
            >
              {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radio className={`w-3 h-3 ${is_online ? "animate-pulse" : ""}`} />}
              {is_online ? "En ligne" : express_enabled ? "Connexion Trigo Express" : "Non intégré"}
            </button>

            <div className="flex items-center gap-3 mt-8">
              <div className="w-16 h-16 rounded-2xl bg-[#F2A93B] text-[#0F2B46] font-extrabold text-2xl flex items-center justify-center border-4 border-white/20" style={{ fontFamily: "Sora" }}>
                {initials}
              </div>
              <div>
                <div className="text-white text-lg font-extrabold" style={{ fontFamily: "Sora" }} data-testid="driver-express-name">{profile.name}</div>
                <div className="flex items-center gap-2 text-[12.5px] text-[#CFE0EF] mt-0.5">
                  <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-[#F2A93B]" fill="#F2A93B" /> {(profile.rating || 0).toFixed(1)}</span>
                  <span>· Trigo {profile.vehicle_label || "Express"}</span>
                  {profile.verified && (
                    <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-300 font-bold text-[10.5px] px-2 py-0.5 rounded-lg"><ShieldCheck className="w-3 h-3" /> Vérifié</span>
                  )}
                </div>
                {is_online && (
                  <div className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 font-bold text-[10px] px-2 py-0.5 rounded-lg mt-1" data-testid="driver-express-persistent-badge">
                    <Radio className="w-2.5 h-2.5" /> Connexion persistante — même après fermeture du navigateur
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stat strip */}
          <div className="grid grid-cols-3 bg-white mx-4 -mt-8 rounded-2xl px-2 py-3.5 shadow-lg relative z-10" data-testid="driver-express-stats">
            <div className="text-center">
              <div className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>{stats.total_trips}</div>
              <div className="text-[10px] text-[#5B6B7A]">Courses</div>
            </div>
            <div className="text-center border-l border-r border-[#E4E0D8]">
              <div className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>{Math.round(stats.accept_rate)}%</div>
              <div className="text-[10px] text-[#5B6B7A]">Acceptation</div>
            </div>
            <div className="text-center">
              <div className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>{Math.round(stats.cancel_rate)}%</div>
              <div className="text-[10px] text-[#5B6B7A]">Annulation</div>
            </div>
          </div>

          {/* Level card */}
          <div className="mx-4 mt-4 rounded-2xl p-4 text-white" style={{ background: "linear-gradient(120deg,#0F2B46,#173A5E)" }} data-testid="driver-express-level">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-extrabold text-sm" style={{ fontFamily: "Sora" }}>
                <span className="text-lg">{level.emoji}</span>
                {level.label}
              </div>
              <span className="text-[11px] text-[#CFE0EF]">
                {level.next ? `${stats.total_trips} / ${level.next} courses` : `${stats.total_trips} courses`}
              </span>
            </div>
            <div className="h-1.5 bg-white/20 rounded mt-3 overflow-hidden">
              <div className="h-full bg-[#F2A93B] rounded transition-all" style={{ width: `${level.progress_pct}%` }} data-testid="driver-express-level-progress" />
            </div>
            <div className="text-[11px] text-[#CFE0EF] mt-2">
              {level.next
                ? `Plus que ${level.next - stats.total_trips} courses avant le niveau suivant · commission réduite à ${level.commission_pct === 15 ? "12" : level.commission_pct}%`
                : `Niveau max atteint · commission ${level.commission_pct}%`}
            </div>
          </div>

          {/* Earnings */}
          <div className="mx-4 mt-4">
            <p className="text-[13px] font-bold uppercase tracking-widest text-[#0F2B46] mb-2" style={{ fontFamily: "Sora" }}>Revenus</p>
            <div className="bg-white border border-[#E4E0D8] rounded-2xl p-3.5" data-testid="driver-express-earnings">
              <div className="flex items-baseline justify-between">
                <span className="text-[22px] font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>{fmtXAF(earnings.week_net_xaf)}</span>
                <span className="text-xs text-[#5B6B7A]">cette semaine (net)</span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-[#E4E0D8] text-[11.5px] text-[#5B6B7A]">
                <span>Brut : {fmtXAF(earnings.week_gross_xaf)} · Commission {earnings.commission_pct}% : {fmtXAF(earnings.week_commission_tft)} TFT</span>
              </div>
            </div>
          </div>

          {/* Vehicle */}
          <div className="mx-4 mt-4">
            <p className="text-[13px] font-bold uppercase tracking-widest text-[#0F2B46] mb-2" style={{ fontFamily: "Sora" }}>Véhicule</p>
            <div className="bg-white border border-[#E4E0D8] rounded-2xl p-3.5 flex items-center gap-3" data-testid="driver-express-vehicle">
              <div className="w-10 h-10 rounded-xl bg-[#F7F5F1] flex items-center justify-center">
                <Bike className="w-5 h-5 text-[#0F2B46]" />
              </div>
              <div className="flex-1 min-w-0">
                {vehicle ? (
                  <>
                    <div className="text-sm font-semibold text-[#142433]">{vehicle.label} — {vehicle.plate}</div>
                    <div className="text-[11.5px] text-[#5B6B7A]">Zone habituelle : {vehicle.zone || "Non précisée"}</div>
                  </>
                ) : (
                  <div className="text-sm text-[#5B6B7A]">Aucun véhicule enregistré. Contactez l&apos;administrateur.</div>
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="mx-4 mt-4">
            <p className="text-[13px] font-bold uppercase tracking-widest text-[#0F2B46] mb-2" style={{ fontFamily: "Sora" }}>Documents</p>
            <div className="bg-white border border-[#E4E0D8] rounded-2xl p-3.5" data-testid="driver-express-documents">
              <DocRow icon={IdCard} title="Permis de conduire" doc={documents.permit} testId="doc-permit" />
              <DocRow icon={FileText} title="Assurance véhicule" doc={documents.insurance} testId="doc-insurance" />
              <DocRow icon={Wrench} title="Visite technique" doc={documents.technical} testId="doc-technical" />
            </div>
          </div>

          {/* Safety */}
          <div className="mx-4 mt-4 mb-4">
            <p className="text-[13px] font-bold uppercase tracking-widest text-[#0F2B46] mb-2" style={{ fontFamily: "Sora" }}>Sécurité</p>
            <div className="bg-white border border-[#E4E0D8] rounded-2xl p-3.5" data-testid="driver-express-safety">
              <div className="flex items-center gap-3 py-1">
                <div className="w-9 h-9 rounded-xl bg-[#F7F5F1] flex items-center justify-center"><PhoneCall className="w-4 h-4 text-[#C1453B]" /></div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#142433]">Contact d&apos;urgence</div>
                  <div className="text-[11.5px] text-[#5B6B7A]">{safety.emergency_contact ? `${safety.emergency_contact} · ${safety.emergency_phone || ""}` : "Aucun contact configuré"}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 py-1 mt-2 pt-2 border-t border-dashed border-[#E4E0D8]">
                <div className="w-9 h-9 rounded-xl bg-[#F7F5F1] flex items-center justify-center"><MapPin className="w-4 h-4 text-[#0F2B46]" /></div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#142433]">Partage de position</div>
                  <div className="text-[11.5px] text-[#5B6B7A]">Actif pendant les courses</div>
                </div>
                <StatusPill status="valid" />
              </div>
            </div>
            {!express_enabled && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Votre compte n&apos;est pas encore intégré à Trigo Express. Contactez l&apos;administrateur pour être ajouté à la plateforme.
                </p>
              </div>
            )}
          </div>

          {/* Bottom nav (static, cosmetic) */}
          <div className="mt-auto grid grid-cols-4 border-t border-[#E4E0D8] bg-white py-3">
            <div className="flex flex-col items-center gap-1 text-[#0F2B46] font-bold text-[10.5px]"><User className="w-4 h-4" />Profil</div>
            <button onClick={() => navigate("/driver?tab=wallet")} className="flex flex-col items-center gap-1 text-[#5B6B7A] text-[10.5px]"><Wallet className="w-4 h-4" />Revenus</button>
            <button onClick={() => navigate("/driver?tab=stats")} className="flex flex-col items-center gap-1 text-[#5B6B7A] text-[10.5px]"><BarChart3 className="w-4 h-4" />Stats</button>
            <button onClick={() => navigate("/driver?tab=profile")} className="flex flex-col items-center gap-1 text-[#5B6B7A] text-[10.5px]"><Settings className="w-4 h-4" />Réglages</button>
          </div>
        </div>
      </div>
    </div>
  );
}
